import PropTypes from 'prop-types';
import Pill from '@/components/vip/VipPill';
import { useVipEntry } from '@/hooks/useVipCustomers';

/**
 * The staff VIP pill beside a customer's name (src/constants/vip.js), with
 * their spend on hover. Renders nothing for anyone who isn't VIP, and
 * nothing at all in a customer's browser (src/hooks/useVipCustomers.js only
 * loads for staff; a customer's own badge is src/components/vip/MyVipBadge).
 */
export default function VipBadge({ userId = null, userIds = null, email = null, className }) {
  const entry = useVipEntry({ userId, userIds, email });
  if (!entry) return null;
  // Under the line but inside their 12 months: say why they're still VIP.
  const keptUntil = entry.spend < entry.lineDollars ? entry.keptUntil : null;
  return <VipPill spend={entry.spend} keptUntil={keptUntil} className={className} />;
}

const keptDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** The pill itself, for rows the server already marked VIP (Live Flights, the Inquiries Desk). */
export function VipPill({ spend = null, keptUntil = null, className }) {
  const amount = spend != null ? `$${Math.round(spend).toLocaleString('en-US')}` : null;
  const kept = keptUntil && Number.isFinite(Date.parse(keptUntil)) ? `, VIP kept until ${keptDate(keptUntil)}` : '';
  return (
    <Pill
      className={className}
      title={amount ? `VIP customer: ${amount} on paid-off and active trips${kept}` : 'VIP customer'}
      srText={amount ? ` customer, ${amount} spent` : ' customer'}
    />
  );
}

VipPill.propTypes = {
  spend: PropTypes.number,
  keptUntil: PropTypes.string,
  className: PropTypes.string,
};

VipBadge.propTypes = {
  userId: PropTypes.string,
  userIds: PropTypes.arrayOf(PropTypes.string),
  email: PropTypes.string,
  className: PropTypes.string,
};
